import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type ClearingInvoiceStatsRequestBody =
  paths['/clearing-invoices/statistics']['post']['requestBody']['content']['application/json'];
export type ClearingInvoiceStatsResponse =
  paths['/clearing-invoices/statistics']['post']['responses']['200']['content']['application/json'];
export type ClearingInvoiceStatsSearchFilters = ClearingInvoiceStatsRequestBody['search'];

export interface ClearingInvoiceStatsParams {
  makerOrganizationUuid: string;
  search?: ClearingInvoiceStatsSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchClearingInvoiceStats = async ({
  makerOrganizationUuid,
  search,
  takerMarkets,
}: ClearingInvoiceStatsParams): Promise<ClearingInvoiceStatsResponse['data']> => {
  const response = await apiClient
    .post(`api/supplier-experience/clearing-invoices/statistics`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((market) => ({
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies ClearingInvoiceStatsRequestBody,
    })
    .json<ClearingInvoiceStatsResponse>();

  return response.data;
};

const useClearingInvoiceStats = (params: ClearingInvoiceStatsParams) => {
  const { makerOrganizationUuid, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['clearing-invoice-stats', makerOrganizationUuid, search, ...takerMarketUuidsMap]
      : ['clearing-invoice-stats-invalid'],
    queryFn: () => (isValid ? fetchClearingInvoiceStats(params) : null),
    select: (data) => {
      return {
        totalAmount: data?.summary.amount ?? 0,
        totalCount: data?.summary.count ?? 0,
      };
    },
  });
};

export default useClearingInvoiceStats;
